import { Channel } from '@/Enums/Channel.enum';
import { ChannelId } from '@/Enums/ChannelId.enum';
import { ShareEmailOptions } from '@/Enums/Forecaster.enum';
import ForecasterControlSection from '@/Enums/Forecaster/ControlSection.enum';
import type { IForecasterChannelTile } from '@/Interfaces/Forecaster/Forecast.interface';
import AmazonIcon from '@/components/Icons/AmazonIcon';
import FourKTvIcon from '@/components/Icons/FourKTvIcon';
import GamingChannelIcon from '@/components/Icons/GamingChannelIcon';
import GoogleIcon from '@/components/Icons/GoogleIcon';
import HeadsetIcon from '@/components/Icons/HeadsetIcon';
import HomeWiFiIcon from '@/components/Icons/HomeWiFiIcon';
import OlvIcon from '@/components/Icons/OlvIcon';
import PulseHeadsetIcon from '@/components/Icons/PulseHeadsetIcon';
import SmartTvIcon from '@/components/Icons/SmartTvIcon';

// GAM forecaster channels — shared across tile gating, geo restriction, section config and payload mapping.
export const GAM_CHANNELS: Channel[] = [Channel.GamVideo, Channel.GamDisplay];
export const GAM_CHANNEL_IDS: ChannelId[] = [ChannelId.GamVideo, ChannelId.GamDisplay];
export const isGamChannel = (channel?: Channel | null): boolean => GAM_CHANNELS.includes(channel as Channel);
export const isGamChannelId = (channelId?: ChannelId | number | null): boolean =>
  GAM_CHANNEL_IDS.includes(channelId as ChannelId);

// Direct Audio picks from the Adswizz catalogue, so its product lives under a different form key.
export const getProductControlName = (channel?: Channel | null): string =>
  channel === Channel.SpotifyDirect ? `${channel}.selectedAdswizzProduct` : `${channel}.selectedProduct`;

export const frequencySliderMaxValue = 20;
export const frequencySliderMinValue = 1;
export const CPMSliderMaxValue = 75;
export const CPMSliderMinValue = 0;

export const CHANNELS: IForecasterChannelTile[] = [
  {
    id: Channel.CTV,
    Icon: FourKTvIcon,
    title: 'Streaming TV',
  },
  {
    id: Channel.OLV,
    Icon: OlvIcon,
    title: 'OLV',
  },
  {
    id: Channel.Display,
    Icon: SmartTvIcon,
    title: 'Display',
  },
  {
    id: Channel.Audio,
    Icon: HeadsetIcon,
    title: 'Audio',
    hidden: true,
  },
  {
    id: Channel.SpotifyDirect,
    Icon: PulseHeadsetIcon,
    title: 'Direct Audio',
    hidden: true,
  },
  {
    id: Channel.Gaming,
    Icon: GamingChannelIcon,
    title: 'Gaming',
  },
  {
    id: Channel.Dooh,
    Icon: HomeWiFiIcon,
    title: 'Digital Out of Home',
    hidden: true,
  },
  {
    id: Channel.Google,
    Icon: GoogleIcon,
    title: 'Google',
  },
  {
    id: Channel.Amazon,
    Icon: AmazonIcon,
    title: 'Amazon',
  },
  {
    id: Channel.GamVideo,
    Icon: FourKTvIcon,
    title: 'GAM Video',
    requiresGamAccount: true,
  },
  {
    id: Channel.GamDisplay,
    Icon: SmartTvIcon,
    title: 'GAM Display',
    requiresGamAccount: true,
  },
];

export const LOADING = 'Loading...';

export const ESTIMATION_CARD_BACKGROUND = 'radial-gradient(143.58% 143.58% at 0% 0%, #7BA695 0%, #1B4332 100%)';

export const MENU_ID = 'forecast-actions-menu';
export const MENU_TRIGGER_ID = 'forecast-actions-menu-trigger';

const commonSectionSelection = [
  ForecasterControlSection.Audience,
  ForecasterControlSection.Products,
  ForecasterControlSection.DealGroups,
  ForecasterControlSection.Duration,
];

export const forecasterControlSections: Record<
  | Channel.Dooh
  | Channel.CTV
  | Channel.OLV
  | Channel.Audio
  | Channel.Gaming
  | Channel.GamVideo
  | Channel.GamDisplay
  | 'default',
  ForecasterControlSection[]
> = {
  [Channel.Dooh]: [ForecasterControlSection.DoohAdFormat, ForecasterControlSection.DoohVenueType],
  [Channel.CTV]: commonSectionSelection,
  [Channel.OLV]: commonSectionSelection,
  [Channel.Audio]: commonSectionSelection,
  [Channel.Gaming]: commonSectionSelection,
  [Channel.GamVideo]: [
    ForecasterControlSection.Products,
    ForecasterControlSection.GamVideoPosition,
    ForecasterControlSection.GamCreativeSize,
  ],
  [Channel.GamDisplay]: [ForecasterControlSection.Products],
  default: [ForecasterControlSection.Audience, ForecasterControlSection.Products],
};

export const RON_PRODUCT = {
  id: 'RON',
  name: 'RON',
  publishers: [],
};

export const FORECASTER_UPDATE_ACTIONS = [
  'geographic',
  'geoFence',
  'geoExclusion',
  'daysInRange',
  'frequencyCapEnabled',
  'frequencyCapPerDay',
  'currentChannel',
];

export const FORECASTER_CALCULATOR_FIELDS = ['calculator.impressions', 'calculator.cpm', 'calculator.budget'];

export const SHARE_EMAIL_OPTIONS = [ShareEmailOptions.WHOLE_ORG, ShareEmailOptions.BY_EMAIL];

export const FORECASTER_SHARING_TEXT = '*This forecast will be shared with all of your team members';

export const DURATIONS = [15, 30, 60, 90];

export const DURATION_OPTIONS = DURATIONS.map(duration => ({
  value: duration,
  label: `${duration} Seconds`,
}));

export const FORECASTER_DOOH_VENUE_TYPE_CHIPS_LIMIT = 15;

export const GAM_VIDEO_POSITIONS: { value: string; label: string }[] = [
  { value: 'PREROLL', label: 'Pre-roll' },
  { value: 'MIDROLL', label: 'Mid-roll' },
  { value: 'POSTROLL', label: 'Post-roll' },
];
